import { lastMessageDebugLog, summarizeLastMessageForLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import type { ChatContact, ChatContactWithChat } from '@/modules/chat-matrix-fast/types'
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import { normalizeAvatarUrl } from '@/modules/chat-matrix-fast/utils/userDataExtractor'
import { settleChatListPreviewReady } from '@/modules/chat-matrix-fast/utils/chatListPreviewState'

/** UI-store API, доступный core-store без прямого импорта chatViewStore. */
export interface ChatViewStoreApi {
  syncWithCoreStore: () => void
  rebuildChatsContactsFromCore: (options?: {
    resort?: boolean
    bumpChatId?: number | string
    patchChatId?: number | string
  }) => void
  updateContactLastMessage: (chatId: number | string, message: IMessage) => void
  moveChatToTop: (chatId: number | string) => void
  forceSortChats: () => void
  removeChatFromUi: (chatId: number | string) => void
  patchMessageReactionsInActiveChat: (messageId: number | string) => void
  syncActiveChatOnline: () => void
  contacts: ChatContact[]
  chatsContacts: ChatContactWithChat[]
}

let getViewStore: (() => ChatViewStoreApi | null) | null = null

export function setChatViewStoreAccessor(accessor: () => ChatViewStoreApi | null): void {
  getViewStore = accessor
}

function view(): ChatViewStoreApi | null {
  return getViewStore?.() ?? null
}

export function syncViewWithCore(): void {
  view()?.syncWithCoreStore()
}

export function getViewChatContactIds(): Set<string> | null {
  const store = view()
  if (!store || store.chatsContacts.length === 0) {
    return null
  }

  return new Set(
    store.chatsContacts.map(contact => String(contact.id)),
  )
}

export function notifyViewLastMessage(
  chatId: number | string,
  message: IMessage,
  options?: { bump?: boolean },
): void {
  const store = view()
  if (!store) {
    return
  }

  lastMessageDebugLog('notifyViewLastMessage → rebuildChatsContactsFromCore', {
    chatId,
    bump: options?.bump !== false,
    message: summarizeLastMessageForLog(message),
  })
  settleChatListPreviewReady(chatId)
  // Пока список ещё не нарисован, не сортируем по частично подгруженным last_message.
  if (store.chatsContacts.length === 0) {
    return
  }
  if (options?.bump === false) {
    store.rebuildChatsContactsFromCore({ patchChatId: chatId })
    return
  }

  // Core уже записал last_message/unread; поднимаем только этот чат, без полной пересортировки.
  store.rebuildChatsContactsFromCore({ bumpChatId: chatId })
}

export function notifyViewForceSortAndSync(): void {
  const store = view()
  if (!store) {
    return
  }

  store.forceSortChats()
  store.syncWithCoreStore()
}

export function notifyViewRemoveChat(chatId: number | string): void {
  view()?.removeChatFromUi(chatId)
}

export function notifyViewSyncActiveChatOnline(): void {
  view()?.syncActiveChatOnline()
}

export function notifyViewPatchReactions(messageId: number | string): void {
  view()?.patchMessageReactionsInActiveChat(messageId)
}

export function notifyViewSyncReactions(messageId?: number | string): void {
  const store = view()
  if (!store) {
    return
  }

  if (messageId != null) {
    store.patchMessageReactionsInActiveChat(messageId)
    return
  }

  store.syncWithCoreStore()
}

export function getViewContacts(): ChatContact[] {
  return view()?.contacts ?? []
}

/** Аватар сотрудника по Matrix MXID из списков контактов UI */
export function findViewAvatarByMatrixUserId(mxid: string): string | null {
  const store = view()
  if (!store || !mxid.trim()) {
    return null
  }

  const normalized = mxid.trim()
  const sources = [...store.chatsContacts, ...store.contacts]

  for (const contact of sources) {
    if (contact.matrixUserId?.trim() === normalized && contact.avatar?.trim()) {
      return normalizeAvatarUrl(contact.avatar)
    }

    const chat = 'chat' in contact ? contact.chat : null
    const peerMxid = chat && 'matrix_peer_id' in chat
      ? String((chat as { matrix_peer_id?: string }).matrix_peer_id || '').trim()
      : ''
    if (peerMxid === normalized && contact.avatar?.trim()) {
      return normalizeAvatarUrl(contact.avatar)
    }
  }

  return null
}
